/**
 * The user-grantable OAuth scopes for the Whop CLI's OAuth app.
 *
 * NOTE: these are OAuth *scope* names, which are NOT the same vocabulary as the
 * REST permission names in the OpenAPI spec's `security` blocks (e.g. products
 * are granted via `access_pass:*`, not `product:*`). Requesting a name the OAuth
 * server doesn't recognize fails the whole login with `invalid_scope`, so only
 * add names confirmed grantable by the OAuth app. Some command groups (ads,
 * audiences, social accounts, referrals, swaps) have no OAuth scope and are only
 * reachable with an API-key login.
 */
export const WHOP_SCOPES = [
	"access_pass:basic:read",
	"access_pass:create",
	"access_pass:delete",
	"access_pass:stats:read",
	"access_pass:update",
	"affiliate:basic:read",
	"affiliate:create",
	"affiliate:update",
	"ai_chat:create",
	"ai_chat:delete",
	"ai_chat:read",
	"ai_chat:update",
	"ai_prompt:create",
	"bounty:create",
	"chat:message:create",
	"chat:moderate",
	"chat:read",
	"checkout_configuration:basic:read",
	"checkout_configuration:create",
	"checkout_configuration:delete",
	"company:authorized_user:email:read",
	"company:authorized_user:read",
	"company:balance:export",
	"company:balance:read",
	"company:basic:read",
	"company:create",
	"company:delete",
	"company:log:read",
	"company:manage_checkout",
	"company:update",
	"company_token_transaction:create",
	"company_token_transaction:read",
	"course_analytics:read",
	"course_lesson_interaction:read",
	"courses:read",
	"courses:update",
	"custom_emoji:update",
	"developer:basic:read",
	"developer:manage_builds",
	"dms:channel:manage",
	"dms:message:manage",
	"dms:read",
	"email",
	"event:create",
	"experience:attach",
	"experience:create",
	"experience:delete",
	"experience:detach",
	"experience:hidden_experience:read",
	"experience:update",
	"forum:moderate",
	"forum:post:create",
	"forum:read",
	"identity:read",
	"identity:write",
	"invoice:basic:read",
	"invoice:create",
	"invoice:update",
	"lead:basic:read",
	"lead:manage",
	"livestream:chat:read",
	"livestream:chat:write",
	"livestream:create",
	"livestream:delete",
	"livestream:manage_recording",
	"livestream:moderate",
	"member:basic:read",
	"member:email:read",
	"member:journey:read",
	"member:manage",
	"member:moderate",
	"member:payment_methods:manage",
	"member:payment_methods:read",
	"member:phone:read",
	"member:stats:read",
	"membership:cancel",
	"membership:update",
	"notification:create",
	"openid",
	"payment:basic:read",
	"payment:charge",
	"payment:dispute",
	"payment:dispute:read",
	"payment:dispute_alert:read",
	"payment:manage",
	"payment:resolution_center",
	"payment:resolution_center_case:read",
	"payment:setup_intent:read",
	"payout:account:read",
	"payout:account:update",
	"payout:create_destination",
	"payout:delete_destination",
	"payout:destination:read",
	"payout:transfer:export",
	"payout:transfer:read",
	"payout:update_destination",
	"payout:withdraw_funds",
	"payout:withdrawal:read",
	"plan:basic:export",
	"plan:basic:read",
	"plan:create",
	"plan:delete",
	"plan:stats:export",
	"plan:stats:read",
	"plan:update",
	"plan:waitlist:export",
	"plan:waitlist:manage",
	"plan:waitlist:read",
	"profile",
	"promo_code:basic:export",
	"promo_code:basic:read",
	"promo_code:create",
	"promo_code:delete",
	"promo_code:update",
	"shipment:basic:read",
	"shipment:create",
	"social_link:update",
	"stats:read",
	"support_chat:create",
	"support_chat:message:create",
	"support_chat:read",
	"tracking_link:basic:export",
	"tracking_link:basic:read",
	"tracking_link:create",
	"tracking_link:delete",
	"tracking_link:stats:export",
	"tracking_link:stats:read",
	"tracking_link:update",
	"user:balance:read",
	"user:profile:update",
] as const;

export type WhopScope = (typeof WHOP_SCOPES)[number];
